import React from 'react';
import { templatePreviewUrl, type Template } from '../../services/template.service';
import { FONT_MONO, FONT_SANS, FONT_SERIF } from './landingData';

export interface TplCard {
    id: string;
    title: string;
    tag: string;
    badge: string | null;
    desc: string;
    keywords: string[];
    uses: string;
    img: string | null;
}

export const toTplCard = (t: Template): TplCard => ({
    id: t.id,
    title: t.name,
    tag: t.category,
    badge: t.badge,
    desc: t.description,
    keywords: t.tags.slice(0, 3),
    uses: `${t.usageCount} ${t.usageCount === 1 ? 'app' : 'apps'} created`,
    img: t.hasPreview ? templatePreviewUrl(t.id) : null,
});

const tw = (v: string) => ({ textWrap: v } as React.CSSProperties);

export const TplBadge: React.FC<{ badge: string }> = ({ badge }) => (
    <div style={{ position: 'absolute', top: 13, left: 13, display: 'flex', alignItems: 'center', gap: 6, height: 26, padding: '0 11px', borderRadius: 999, background: 'var(--hv-card)', fontFamily: FONT_MONO, fontSize: 9.5, letterSpacing: '0.06em', color: 'var(--hv-mint-ink)', pointerEvents: 'none', boxShadow: '0 8px 16px -12px var(--hv-shadow)' }}>
        <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2l2 8 8 2-8 2-2 8-2-8-8-2 8-2z" /></svg>
        {badge.toUpperCase()}
    </div>
);

export const TplArrow = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h13M13 6l6 6-6 6" /></svg>
);

/**
 * One template in the catalog (landing section and /templates). With `onOpen`,
 * the preview and title open the template's details.
 */
export const TemplateCard: React.FC<{ t: TplCard; busyId: string | null; onUse: (id: string) => void; onOpen?: (id: string) => void; ring?: boolean }> = ({ t, busyId, onUse, onOpen, ring }) => {
    const openProps = onOpen
        ? { role: 'button' as const, tabIndex: 0, onClick: () => onOpen(t.id), onKeyDown: (e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(t.id); } }, 'aria-label': `${t.title} details`, 'data-track': 'template_details', 'data-track-label': t.id }
        : {};
    return (
        <div data-tpl-id={t.id} className={ring ? 'hv-h-lift5 hv-tpl-ring' : 'hv-h-lift5'} style={{ display: 'flex', flexDirection: 'column', borderRadius: 22, overflow: 'hidden', background: 'var(--hv-card)', border: `1px solid ${ring ? 'var(--brand,#13422C)' : 'var(--hv-line)'}`, boxShadow: '0 30px 50px -42px var(--hv-shadow)', transition: 'transform 0.35s cubic-bezier(0.2,0.9,0.24,1),box-shadow 0.35s ease,border-color 0.3s ease' }}>
            <div {...openProps} style={{ position: 'relative', height: 204, background: 'var(--hv-page-2)', cursor: onOpen ? 'pointer' : undefined }}>
                {t.img ? (
                    <img src={t.img} alt={`${t.title} preview`} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                ) : (
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'flex-end', padding: 18, background: 'linear-gradient(150deg,var(--hv-mint),var(--hv-page-2))' }}>
                        <span style={{ fontFamily: FONT_SERIF, fontSize: 28, lineHeight: 1.05, color: 'var(--hv-mint-ink)', opacity: 0.6 }}>{t.title}</span>
                    </div>
                )}
                {t.badge && <TplBadge badge={t.badge} />}
            </div>
            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                    <h3 style={{ margin: 0, fontFamily: FONT_SANS, fontWeight: 700, fontSize: 18, lineHeight: 1.15, letterSpacing: '-0.01em', color: 'var(--hv-ink)' }}>
                        {onOpen ? (
                            <button type="button" onClick={() => onOpen(t.id)} className="hv-h-underline" style={{ border: 0, padding: 0, background: 'transparent', font: 'inherit', color: 'inherit', cursor: 'pointer', textAlign: 'left' }}>{t.title}</button>
                        ) : t.title}
                    </h3>
                    <div style={{ flex: 1 }} />
                    <span style={{ fontFamily: FONT_MONO, fontSize: 9.5, letterSpacing: '0.06em', color: 'var(--hv-muted)' }}>{t.tag.toUpperCase()}</span>
                </div>
                <p style={{ margin: '10px 0 0', fontSize: 13.5, lineHeight: 1.6, color: 'var(--hv-body)', ...tw('pretty') }}>{t.desc}</p>
                <div style={{ display: 'flex', gap: 6, marginTop: 16, flexWrap: 'wrap' }}>
                    {t.keywords.map((k) => (
                        <span key={k} style={{ height: 25, display: 'flex', alignItems: 'center', padding: '0 10px', borderRadius: 999, background: 'var(--hv-page-2)', fontSize: 11.5, fontWeight: 500, color: 'var(--hv-body)' }}>{k}</span>
                    ))}
                </div>
                <div style={{ flex: 1, minHeight: 18 }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingTop: 16, borderTop: '1px dashed var(--hv-line-2)' }}>
                    <span style={{ fontSize: 12, color: 'var(--hv-muted)' }}>{t.uses}</span>
                    <div style={{ flex: 1 }} />
                    <UseButton id={t.id} busyId={busyId} onUse={onUse} />
                </div>
            </div>
        </div>
    );
};

export const UseButton: React.FC<{ id: string; busyId: string | null; onUse: (id: string) => void; label?: string; big?: boolean }> = ({ id, busyId, onUse, label = 'Use this', big }) => (
    <button type="button" disabled={!!busyId} data-track="template_use" data-track-label={id} onClick={() => onUse(id)} className="hv-h-brand-dark" style={{ display: 'flex', alignItems: 'center', gap: 7, height: big ? 46 : 38, padding: big ? '0 22px' : '0 16px', borderRadius: 999, border: 0, color: '#FFFDF7', fontSize: big ? 14 : 13, fontWeight: 700, cursor: busyId ? 'default' : 'pointer', background: 'var(--brand,#13422C)', opacity: busyId && busyId !== id ? 0.6 : 1 }}>
        {busyId === id ? 'Setting up…' : label}
        <TplArrow />
    </button>
);
